/**
 * DeviceList - Display user's devices with verification status
 */

import { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CryptoEvent } from '../../constants/matrixEvents';
import { useMatrixClient } from '../../context/MatrixContext';
import { useModal } from '../../context/ModalContext';
import { useI18n } from '../../context/I18nContext';
import VerifiedBadge from './VerifiedBadge';
import VerificationModal from '../verification/VerificationModal';
import PasswordInputModal from '../Modal/PasswordInputModal';
import { useDeviceVerificationStatus } from '../../hooks/useDeviceVerificationStatus';
import matrixService from '../../services/matrixService';
import { openSSOFallbackAuth } from '../../services/ssoService';
import * as credentialStore from '../../services/credentialStore';
import { AUTH_ERROR_MESSAGES, isUserAuthCancellation } from '../../utils/authErrors';
import logger from '../../utils/logger';
import './DeviceList.css';

// SSO UIA retry cadence — mirrors SecretStorageService constants.
// After the SSO popup resolves the server may take a moment to mark the
// session complete, so we poll the DELETE with { session } every 2s for up
// to 2 minutes before surfacing a failure.
const SSO_RETRY_INTERVAL = 2000;
const SSO_RETRY_TIMEOUT = 2 * 60 * 1000;

function readSsoHostHints() {
  let homeserverUrl;
  let loginHint;
  try {
    const creds = credentialStore.readCredentials();
    homeserverUrl = creds?.homeserverUrl;
    loginHint = localStorage.getItem('ssoLoginEmail') || undefined;
  } catch {
    // Fall back to ssoService defaults
  }
  return { homeserverUrl, loginHint };
}

function abortableDelay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new Error(AUTH_ERROR_MESSAGES.ABORTED));
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error(AUTH_ERROR_MESSAGES.ABORTED));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// Time threshold (in milliseconds) for considering timestamps "significantly different"
// Prevents visual reordering from minor timestamp changes during sync
const SIGNIFICANT_TIME_DIFF_MS = 5 * 60 * 1000; // 5 minutes

/**
 * DeviceListItem Component
 * Wrapped in React.memo to prevent re-renders when parent re-renders with same props.
 * This is critical to prevent cascading re-renders during verification completion
 * when multiple VERIFICATION_COMPLETE events trigger rapid parent updates.
 */
const DeviceListItem = memo(function DeviceListItem({ device, isCurrentDevice, isDehydrated, onVerify, onLogout, verificationCompleted }) {
  const { t } = useI18n();
  const { isVerified, isCrossSigned } = useDeviceVerificationStatus(
    device.userId,
    device.deviceId,
  );

  return (
    <div className={`device-list-item ${isCurrentDevice ? 'device-list-item--current' : ''} ${isDehydrated ? 'device-list-item--dehydrated' : ''}`}>
      <div className="device-list-item__info">
        <div className="device-list-item__header">
          <h4 className="device-list-item__name">
            {isDehydrated ? t('DeviceListItem.dehydratedDeviceName') : (device.displayName || device.deviceId)}
            {isCurrentDevice && <span className="device-list-item__current-badge">{t('DeviceListItem.currentDeviceBadge')}</span>}
          </h4>
          <VerifiedBadge
            isVerified={isVerified}
            isCrossSigned={isCrossSigned}
            size="small"
          />
        </div>
        <div className="device-list-item__details">
          <span className="device-list-item__id">{t('DeviceListItem.idLabel')} {device.deviceId}</span>
          {device.lastSeenTs && (
            <span className="device-list-item__last-seen">
              {t('DeviceListItem.lastSeenLabel')} {new Date(device.lastSeenTs).toLocaleString()}
            </span>
          )}
        </div>
        {isDehydrated && (
          <div className="device-list-item__dehydration-note" role="note">
            <ion-icon name="information-circle-outline"></ion-icon>
            <span>{t('DeviceListItem.dehydrationWarning')}</span>
          </div>
        )}
      </div>
      <div className="device-list-item__actions">
        {!isVerified && isCurrentDevice && !verificationCompleted && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onVerify(device)}
          >
            {t('DeviceListItem.verifyNow')}
          </button>
        )}
        {!isCurrentDevice && (
          <button
            className="device-logout-button"
            onClick={() => onLogout(device)}
            title={t('DeviceListItem.signOutDevice')}
            aria-label={t('DeviceListItem.signOutDevice')}
          >
            {t('DeviceListItem.logout')}
          </button>
        )}
      </div>
    </div>
  );
});

DeviceListItem.propTypes = {
  device: PropTypes.shape({
    userId: PropTypes.string,
    deviceId: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    lastSeenTs: PropTypes.number,
    isDehydrated: PropTypes.bool,
  }).isRequired,
  isCurrentDevice: PropTypes.bool,
  isDehydrated: PropTypes.bool,
  onVerify: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  verificationCompleted: PropTypes.bool,
};

/**
 * DeviceList Component
 *
 * Displays a list of user's Matrix devices with their verification status.
 * Updates automatically when devices are added/removed via CryptoEvent.DevicesUpdated.
 *
 * @param {Object} props
 * @param {string} [props.userId] - User ID to fetch devices for (defaults to current user)
 * @param {Function} [props.onRefreshReady] - Callback to provide refresh function to parent
 */
const DeviceList = ({ userId, onRefreshReady }) => {
  const client = useMatrixClient();
  const { t } = useI18n();
  const { showConfirm, showError: showErrorModal, showInfo } = useModal();
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  // Track verification completion to prevent loop from re-renders during async operations
  const [verificationCompleted, setVerificationCompleted] = useState(false);
  const [passwordModalState, setPasswordModalState] = useState({
    isOpen: false,
    deviceId: null,
    deviceName: null,
  });
  const [ssoAuthInProgress, setSsoAuthInProgress] = useState(false);

  // Ref to avoid stale closure in password handler
  const passwordModalStateRef = useRef(passwordModalState);
  useEffect(() => {
    passwordModalStateRef.current = passwordModalState;
  }, [passwordModalState]);

  // AbortController tracks the in-flight SSO popup + polling so we can cancel
  // cleanly if the component unmounts mid-flow.
  const ssoAbortRef = useRef(null);
  useEffect(() => {
    return () => {
      if (ssoAbortRef.current) {
        ssoAbortRef.current.abort();
        ssoAbortRef.current = null;
      }
    };
  }, []);

  const currentDeviceId = client?.getDeviceId();
  const currentUserId = userId || client?.getUserId();

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    if (!client || !currentUserId) {
      setError(t('DeviceList.clientNotInitialized'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get devices for user
      // Always use REST API to get ALL devices from server (matches Element Web behavior)
      // crypto.getUserDeviceInfo() only returns devices with crypto keys, missing some devices
      let deviceList = [];
      let cryptoDevicesMap = null;

      try {
        // Primary method: Use REST API to get all devices
        const response = await client.getDevices();
        deviceList = response.devices.map(device => ({
          userId: currentUserId,
          deviceId: device.device_id,
          displayName: device.display_name || device.device_id,
          lastSeenTs: device.last_seen_ts,
        }));
      } catch (err) {
        // Fallback: Try crypto API if REST API fails
        logger.warn('DeviceList: REST API failed, trying crypto API:', err);
        const crypto = client.getCrypto();
        if (crypto?.getUserDeviceInfo) {
          cryptoDevicesMap = await crypto.getUserDeviceInfo([currentUserId]);
          const userDevices = cryptoDevicesMap.get(currentUserId);

          if (userDevices) {
            deviceList = Array.from(userDevices.values()).map(device => ({
              userId: currentUserId,
              deviceId: device.deviceId,
              displayName: device.displayName || device.deviceId,
              lastSeenTs: device.lastSeenTs,
            }));
          }
        }
      }

      // Enrich devices with dehydration status from crypto API
      const crypto = client.getCrypto();
      if (crypto?.getUserDeviceInfo) {
        try {
          const devicesMap = cryptoDevicesMap || await crypto.getUserDeviceInfo([currentUserId]);
          const userDevices = devicesMap.get(currentUserId);
          if (userDevices) {
            const dehydrationMap = new Map();
            for (const [deviceId, device] of userDevices) {
              dehydrationMap.set(deviceId, device.dehydrated || false);
            }
            deviceList = deviceList.map(device => ({
              ...device,
              isDehydrated: dehydrationMap.get(device.deviceId) || false,
            }));
          }
        } catch (err) {
          logger.warn('DeviceList: Failed to get dehydration status:', err);
        }
      }

      /**
       * Sort devices with stable ordering to prevent visual jumping:
       * 1. Current device always appears first
       * 2. Other devices sorted by last seen time (most recent first)
       * 3. Only reorder if timestamp difference is significant (> 5 minutes)
       * 4. Fall back to alphabetical sort by deviceId for stability
       */
      deviceList.sort((a, b) => {
        // Current device always first
        if (a.deviceId === currentDeviceId) return -1;
        if (b.deviceId === currentDeviceId) return 1;
        
        // Sort by last seen timestamp, but only if difference is significant
        const timeDiff = (b.lastSeenTs || 0) - (a.lastSeenTs || 0);
        
        // Only reorder if timestamps differ by more than the threshold
        // This prevents visual reordering from minor timestamp updates during sync
        if (Math.abs(timeDiff) > SIGNIFICANT_TIME_DIFF_MS) {
          return timeDiff;
        }
        
        // For similar timestamps, use stable deviceId sort
        return a.deviceId.localeCompare(b.deviceId);
      });

      setDevices(deviceList);
      logger.debug(`DeviceList: Loaded ${deviceList.length} devices`);

    } catch (err) {
      logger.error('DeviceList: Failed to fetch devices:', err);
      setError(t('DeviceList.failedLoadDevices'));
    } finally {
      setIsLoading(false);
    }
  }, [client, currentUserId, currentDeviceId, t]);

  // Initial fetch
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Provide refresh function to parent component
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(fetchDevices);
    }
  }, [fetchDevices, onRefreshReady]);

  // Note: Removed sync event listener to prevent periodic updates.
  // Device list now only updates on:
  // 1. Initial load
  // 2. Manual refresh button click
  // 3. After verification completes
  // 4. When devices are added/removed (via CryptoEvent.DevicesUpdated)
  // Device verification status still updates in real-time via trust events.

  // Ref to track debounce timeout for DevicesUpdated events
  const devicesUpdatedTimeoutRef = useRef(null);

  // Listen for device changes (when devices are added/removed)
  // Uses debouncing to prevent rapid re-renders during verification completion
  // when crypto layer fires multiple DevicesUpdated events in quick succession
  useEffect(() => {
    if (!client) return;

    const handleDevicesUpdated = (users, initialFetch) => {
      // Only refresh if it's the current user's devices and not initial fetch
      // This captures when devices are added/removed from other clients
      if (!initialFetch && users && users.includes(currentUserId)) {
        logger.debug('DeviceList: Devices updated for current user');

        // Debounce: Clear any pending update and schedule a new one
        // This batches rapid DevicesUpdated events (e.g., during verification completion)
        // into a single fetchDevices() call after 300ms of quiet time
        if (devicesUpdatedTimeoutRef.current) {
          clearTimeout(devicesUpdatedTimeoutRef.current);
        }
        devicesUpdatedTimeoutRef.current = setTimeout(() => {
          devicesUpdatedTimeoutRef.current = null;
          fetchDevices();
        }, 300);
      }
    };

    client.on(CryptoEvent.DevicesUpdated, handleDevicesUpdated);

    return () => {
      client.off(CryptoEvent.DevicesUpdated, handleDevicesUpdated);
      // Clean up any pending timeout on unmount
      if (devicesUpdatedTimeoutRef.current) {
        clearTimeout(devicesUpdatedTimeoutRef.current);
      }
    };
  }, [client, currentUserId, fetchDevices]);

  // Handle verify action
  const handleVerify = useCallback(() => {
    logger.info('User initiated device verification');
    setShowVerificationModal(true);
  }, []);

  // Stable callback for closing modal - prevents new function reference on each render
  // which would cause VerificationModal to see changed props and potentially re-render
  const handleModalClose = useCallback(() => {
    setShowVerificationModal(false);
  }, []);

  // Handle verification complete
  const handleVerificationComplete = useCallback((success) => {
    if (success) {
      // Mark as completed BEFORE closing modal to prevent race condition
      // This matches the Chat Backup flow's use of verified state
      setVerificationCompleted(true);
    }

    setShowVerificationModal(false);

    if (success) {
      // Refresh device list to show updated status
      fetchDevices();
    }
  }, [fetchDevices]);

  const runSsoDeviceLogout = useCallback(async (deviceId, deviceName, session) => {
    // Abort any previous run before starting a new one
    if (ssoAbortRef.current) {
      ssoAbortRef.current.abort();
    }
    const controller = new AbortController();
    ssoAbortRef.current = controller;
    setSsoAuthInProgress(true);

    try {
      const { homeserverUrl, loginHint } = readSsoHostHints();
      await openSSOFallbackAuth(session, controller.signal, homeserverUrl, loginHint);

      // Poll the DELETE with { session } until the server marks the UIA complete
      const deadline = Date.now() + SSO_RETRY_TIMEOUT;
      let attempt = 0;
      for (;;) {
        if (controller.signal.aborted) throw new Error(AUTH_ERROR_MESSAGES.ABORTED);
        attempt++;
        try {
          const retry = await matrixService.deleteDevice(deviceId, { ssoSession: session });
          if (retry.success) {
            showInfo(t('DeviceList.success'), t('DeviceList.signOutSuccess', { deviceName }));
            fetchDevices();
            return;
          }
          // Non-401 failure surfaced via result — stop polling
          showErrorModal(t('DeviceList.signOutFailed'), retry.message || t('DeviceList.ssoAuthFailed'));
          return;
        } catch (retryErr) {
          if (retryErr.httpStatus !== 401 || Date.now() >= deadline) {
            logger.warn('DeviceList: SSO UIA retry failed permanently', {
              attempt,
              httpStatus: retryErr.httpStatus,
              message: retryErr.message,
            });
            showErrorModal(t('DeviceList.signOutFailed'), t('DeviceList.ssoAuthFailed'));
            return;
          }
          await abortableDelay(SSO_RETRY_INTERVAL, controller.signal);
        }
      }
    } catch (ssoErr) {
      if (isUserAuthCancellation(ssoErr)) {
        logger.info('DeviceList: SSO sign-out cancelled by user');
        return; // silent cancel
      }
      if (ssoErr.message === AUTH_ERROR_MESSAGES.SSO_POPUP_BLOCKED) {
        logger.warn('DeviceList: SSO popup was blocked by the browser');
        showErrorModal(t('DeviceList.signOutFailed'), t('DeviceList.ssoPopupBlocked'));
        return;
      }
      logger.error('DeviceList: SSO device logout failed:', ssoErr.message);
      showErrorModal(t('DeviceList.signOutFailed'), t('DeviceList.ssoAuthFailed'));
    } finally {
      // Only reset banner state if we are the currently-tracked controller.
      // A concurrent Sign Out click would have replaced ssoAbortRef.current with
      // a new controller; its cleanup must stay in charge of the banner.
      if (ssoAbortRef.current === controller) {
        ssoAbortRef.current = null;
        setSsoAuthInProgress(false);
      }
    }
  }, [fetchDevices, showErrorModal, showInfo, t]);

  // Handle device logout
  const handleDeviceLogout = useCallback((device) => {
    const deviceName = device.displayName || device.deviceId;

    // Show confirmation dialog
    showConfirm(
      t('DeviceList.signOutDeviceConfirmTitle'),
      t('DeviceList.signOutDeviceConfirmMessage', { deviceName }),
      async () => {
        try {
          // First try without auth (uses HTTP cookie if valid)
          const result = await matrixService.deleteDevice(device.deviceId);

          // SSO UIA required — route through the fallback popup (NED-3498)
          if (!result.success && result.needsSsoAuth) {
            logger.debug('SSO re-auth required, opening SSO fallback popup');
            await runSsoDeviceLogout(device.deviceId, deviceName, result.session);
            return;
          }

          // If password needed, show modal
          if (!result.success && result.needsPassword) {
            logger.debug('Password required, showing password modal');
            setPasswordModalState({
              isOpen: true,
              deviceId: device.deviceId,
              deviceName: deviceName,
            });
            return;  // Wait for modal callback
          }

          // Handle success (HTTP cookie was valid)
          if (result.success) {
            showInfo(t('DeviceList.success'), t('DeviceList.signOutSuccess', { deviceName }));
            fetchDevices();
          } else {
            showErrorModal(t('DeviceList.signOutFailed'), result.message);
          }
        } catch (err) {
          logger.error('Error in device logout:', err);
          showErrorModal(t('Modal.error'), t('DeviceList.unexpectedError'));
        }
      },
    );
  }, [showConfirm, showErrorModal, showInfo, fetchDevices, runSsoDeviceLogout, t]);

  // Handle password submission from modal
  const handlePasswordSubmit = useCallback(async (password) => {
    // Get current values from ref to avoid stale closure
    const { deviceId, deviceName } = passwordModalStateRef.current;
    
    // Close modal immediately before async operation
    setPasswordModalState({ isOpen: false, deviceId: null, deviceName: null });
    
    try {
      // Retry with password
      const result = await matrixService.deleteDevice(deviceId, password);
      
      if (result.success) {
        showInfo(t('DeviceList.success'), t('DeviceList.signOutSuccess', { deviceName }));
        fetchDevices();
      } else {
        const errorMessage = result.errorCode === 'INCORRECT_PASSWORD'
          ? t('DeviceList.incorrectPassword')
          : result.message;
        showErrorModal(t('DeviceList.signOutFailed'), errorMessage);
      }
    } catch (err) {
      logger.error('Error in device logout with password:', err);
      showErrorModal(t('Modal.error'), t('DeviceList.unexpectedError'));
    }
  }, [showInfo, showErrorModal, fetchDevices, t]);

  const handlePasswordCancel = useCallback(() => {
    logger.debug('User cancelled password input');
    setPasswordModalState({ isOpen: false, deviceId: null, deviceName: null });
  }, []);

  // Memoize the VerificationModal element to prevent unmount/remount during rapid re-renders
  // When DeviceList re-renders (e.g., from device updates), this ensures the modal element
  // maintains the same identity, preventing React from unmounting and remounting it.
  // Dependencies: showVerificationModal (to update isOpen), stable callbacks
  const verificationModalElement = useMemo(() => (
    <VerificationModal
      isOpen={showVerificationModal}
      onClose={handleModalClose}
      onComplete={handleVerificationComplete}
    />
  ), [showVerificationModal, handleModalClose, handleVerificationComplete]);

  // Display all devices
  const displayedDevices = devices;

  // Render device list content based on state
  // NOTE: Modal is rendered separately below to ensure it persists across all states
  const renderDeviceListContent = () => {
    if (isLoading) {
      return (
        <div className="device-list device-list--loading">
          <div className="spinner" />
          <p>{t('DeviceList.loadingDevices')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="device-list device-list--error">
          <p className="error-message">{error}</p>
          <button className="btn btn-primary" onClick={fetchDevices}>
            {t('DeviceList.tryAgain')}
          </button>
        </div>
      );
    }

    if (displayedDevices.length === 0) {
      return (
        <div className="device-list device-list--empty">
          <p>{t('DeviceList.noDevicesFound')}</p>
        </div>
      );
    }

    return (
      <div className="device-list">
        <div className="device-list__items">
          {displayedDevices.map(device => (
            <DeviceListItem
              key={device.deviceId}
              device={device}
              isCurrentDevice={device.deviceId === currentDeviceId}
              isDehydrated={device.isDehydrated}
              onVerify={handleVerify}
              onLogout={handleDeviceLogout}
              verificationCompleted={verificationCompleted}
            />
          ))}
        </div>
      </div>
    );
  };

  // Always render the same structure to ensure modals persist across state changes
  // The device list content changes based on loading/error/empty state,
  // but modals are always rendered to prevent unmount/remount flashing
  return (
    <>
      {ssoAuthInProgress && (
        <div className="device-list__sso-progress" role="status" aria-live="polite">
          <div className="spinner" />
          <span>{t('DeviceList.ssoAuthInProgress')}</span>
        </div>
      )}
      {renderDeviceListContent()}

      {/* Render memoized modal element to prevent unmount/remount during concurrent renders */}
      {/* The element is memoized above with stable callbacks to maintain identity */}
      {verificationModalElement}

      {/* Password input modal */}
      {/* CRITICAL: Use deviceId as key to force new component instance per device */}
      {/* This ensures password field is cleared when modal opens for a different device */}
      <PasswordInputModal
        key={passwordModalState.deviceId || 'closed'}
        isOpen={passwordModalState.isOpen}
        title={t('DeviceList.enterPasswordTitle')}
        message={t('DeviceList.signOutDeviceMessage', { deviceName: passwordModalState.deviceName || 'this device' })}
        confirmText={t('DeviceList.signOut')}
        cancelText={t('Modal.cancel')}
        onConfirm={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
      />
    </>
  );
};

DeviceList.propTypes = {
  userId: PropTypes.string,
  onRefreshReady: PropTypes.func,
};

export default DeviceList;